import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useTranslation } from 'react-i18next';
import { useInstatDesign } from '../theme/instatDesign';

const TRACK_HEIGHT = 28;
const TRACK_GAP = 4;
const HEADER_WIDTH = 110;
const MIN_DURATION = 0.1;

function formatSec(s) {
  if (!Number.isFinite(s)) return '0.0s';
  return `${s.toFixed(1)}s`;
}

export default function TelestrationTimeline({ duration, currentTime, onSeek, scene, sceneApi }) {
  const d = useInstatDesign();
  const { t } = useTranslation();
  const railRef = useRef(null);
  const [railWidth, setRailWidth] = useState(0);
  const [drag, setDrag] = useState(null); // { id, mode: 'move'|'left'|'right', startX, startTIn, startTOut }

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    const update = () => setRailWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pxPerSec = duration > 0 && railWidth > 0 ? railWidth / duration : 0;
  const hasObjects = scene?.objects?.length > 0;
  const playheadPx = duration > 0 ? (currentTime / duration) * railWidth : 0;

  // Scrub on empty rail area
  const handleRailClick = useCallback((e) => {
    const rail = railRef.current;
    if (!rail || !duration) return;
    // Ignore if clicking on a track segment
    if (e.target.closest('[data-track-segment]')) return;
    const rect = rail.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    onSeek?.(Math.max(0, Math.min(1, ratio)) * duration);
  }, [duration, onSeek]);

  // Global mouse tracking for segment drag
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      if (!duration || !pxPerSec) return;
      const dxPx = e.clientX - drag.startX;
      const dxSec = dxPx / pxPerSec;

      let tIn = drag.startTIn;
      let tOut = drag.startTOut;

      if (drag.mode === 'move') {
        const span = tOut - tIn;
        tIn = Math.max(0, Math.min(duration - span, drag.startTIn + dxSec));
        tOut = tIn + span;
      } else if (drag.mode === 'left') {
        tIn = Math.max(0, Math.min(drag.startTOut - MIN_DURATION, drag.startTIn + dxSec));
      } else if (drag.mode === 'right') {
        tOut = Math.max(drag.startTIn + MIN_DURATION, Math.min(duration, drag.startTOut + dxSec));
      }

      sceneApi.updateObjectTiming(drag.id, tIn, tOut);
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, duration, pxPerSec, sceneApi]);

  const startDrag = (e, obj, mode) => {
    e.stopPropagation();
    setDrag({
      id: obj.id,
      mode,
      startX: e.clientX,
      startTIn: obj.tIn ?? 0,
      startTOut: obj.tOut ?? duration,
    });
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        maxHeight: 260,
        bgcolor: d.colors.surface,
        borderTop: `1px solid ${d.colors.divider}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${d.colors.divider}` }}>
        <Typography sx={{ ...d.typo.micro, color: d.colors.textTertiary }}>
          {t('instat.telestration.timeline')}
        </Typography>
        <Typography sx={{ ...d.typo.caption }}>
          {hasObjects
            ? t('instat.telestration.annotationCount', { count: scene.objects.length })
            : t('instat.telestration.noAnnotations')}
        </Typography>

        {/* Default duration selector */}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ ...d.typo.caption }}>{t('instat.telestration.defaultDuration')}</Typography>
        {[0.5, 1, 2, 3, 5].map((s) => (
          <Box
            key={s}
            onClick={() => sceneApi.setDefaultDuration(s)}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              px: 1,
              py: 0.25,
              borderRadius: `${d.radius.sm}px`,
              cursor: 'pointer',
              bgcolor: scene.defaultDuration === s ? d.accentSoft : 'transparent',
              color: scene.defaultDuration === s ? d.accent : d.colors.textSecondary,
              '&:hover': { bgcolor: scene.defaultDuration === s ? d.accentSoft : d.colors.canvasAlt },
            }}
          >
            {s}s
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', overflow: 'hidden' }}>
        {/* Track headers (labels) */}
        <Box
          sx={{
            width: HEADER_WIDTH,
            flexShrink: 0,
            borderRight: `1px solid ${d.colors.divider}`,
            maxHeight: 180,
            overflowY: 'auto',
          }}
        >
          <Box sx={{ height: 22, borderBottom: `1px solid ${d.colors.divider}`, bgcolor: d.colors.canvasAlt }} />
          {scene.objects.map((obj) => {
            const selected = obj.id === scene.selectedId;
            return (
              <Box
                key={obj.id}
                onClick={() => sceneApi.selectObject(obj.id)}
                sx={{
                  height: TRACK_HEIGHT,
                  marginTop: `${TRACK_GAP}px`,
                  px: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  bgcolor: selected ? d.accentSoft : 'transparent',
                  borderLeft: `3px solid ${obj.props?.stroke || d.accent}`,
                  '&:hover': { bgcolor: selected ? d.accentSoft : d.colors.canvasAlt },
                }}
              >
                <Typography noWrap sx={{ fontSize: 11, fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {t(`instat.telestration.shapeTypes.${obj.type}`, obj.type)}
                </Typography>
                {obj.keyframes && obj.keyframes.length > 0 && (
                  <Tooltip
                    title={t('instat.telestration.keyframeCountTip', { count: obj.keyframes.length })}
                  >
                    <Box sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: obj.props?.stroke || d.accent,
                      bgcolor: 'rgba(0,0,0,0.05)',
                      borderRadius: '10px',
                      px: 0.6,
                      py: 0.1,
                    }}>
                      ◆{obj.keyframes.length}
                    </Box>
                  </Tooltip>
                )}
                <Tooltip title={t('instat.telestration.deleteShape')}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); sceneApi.deleteObject(obj.id); }}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>

        {/* Tracks rail */}
        <Box
          ref={railRef}
          onClick={handleRailClick}
          sx={{
            flex: 1,
            position: 'relative',
            bgcolor: d.colors.canvasAlt,
            cursor: 'pointer',
            minHeight: 80,
            maxHeight: 180,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {/* Time ruler */}
          <Box sx={{ height: 22, position: 'sticky', top: 0, bgcolor: d.colors.canvasAlt, borderBottom: `1px solid ${d.colors.divider}`, zIndex: 2 }}>
            {duration > 0 && Array.from({ length: Math.floor(duration) + 1 }).map((_, i) => (
              <Box
                key={i}
                sx={{
                  position: 'absolute',
                  left: `${(i / duration) * 100}%`,
                  top: 0,
                  bottom: 0,
                  borderLeft: `1px solid ${d.colors.divider}`,
                  pl: 0.5,
                  fontSize: 10,
                  color: d.colors.textTertiary,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {i}s
              </Box>
            ))}
          </Box>

          {/* Object tracks */}
          {scene.objects.map((obj, idx) => {
            const tIn = obj.tIn ?? 0;
            const tOut = obj.tOut ?? duration;
            const leftPct = duration > 0 ? (tIn / duration) * 100 : 0;
            const widthPct = duration > 0 ? ((tOut - tIn) / duration) * 100 : 0;
            const selected = obj.id === scene.selectedId;
            const color = obj.props?.stroke || d.accent;

            return (
              <Box
                key={obj.id}
                sx={{
                  position: 'relative',
                  height: TRACK_HEIGHT,
                  marginTop: `${TRACK_GAP}px`,
                }}
              >
                <Box
                  data-track-segment
                  onMouseDown={(e) => startDrag(e, obj, 'move')}
                  onClick={(e) => { e.stopPropagation(); sceneApi.selectObject(obj.id); }}
                  sx={{
                    position: 'absolute',
                    left: `${leftPct}%`,
                    width: `${widthPct}%`,
                    top: 2,
                    bottom: 2,
                    bgcolor: color,
                    opacity: selected ? 0.85 : 0.55,
                    border: selected ? `2px solid ${d.colors.text}` : `1px solid rgba(0,0,0,0.15)`,
                    borderRadius: `${d.radius.xs}px`,
                    cursor: 'grab',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  {/* Left handle */}
                  <Box
                    onMouseDown={(e) => startDrag(e, obj, 'left')}
                    sx={{
                      position: 'absolute',
                      left: 0, top: 0, bottom: 0, width: 6,
                      cursor: 'ew-resize',
                      bgcolor: 'rgba(0,0,0,0.25)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.45)' },
                    }}
                  />
                  {/* Right handle */}
                  <Box
                    onMouseDown={(e) => startDrag(e, obj, 'right')}
                    sx={{
                      position: 'absolute',
                      right: 0, top: 0, bottom: 0, width: 6,
                      cursor: 'ew-resize',
                      bgcolor: 'rgba(0,0,0,0.25)',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.45)' },
                    }}
                  />
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#fff', px: 1, textShadow: '0 1px 2px rgba(0,0,0,0.5)', whiteSpace: 'nowrap' }}>
                    {formatSec(tIn)}–{formatSec(tOut)}
                  </Typography>
                </Box>

                {/* Keyframe dots — positioned relative to the full rail, NOT the segment */}
                {(obj.keyframes || []).map((k, ki) => {
                  if (duration <= 0) return null;
                  const kLeft = (k.t / duration) * 100;
                  return (
                    <Box
                      key={ki}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSeek?.(k.t);
                        sceneApi.selectObject(obj.id);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        sceneApi.deleteKeyframe(obj.id, k.t);
                      }}
                      title={t('instat.telestration.keyframeTooltip', { time: formatSec(k.t) })}
                      sx={{
                        position: 'absolute',
                        left: `${kLeft}%`,
                        top: '50%',
                        transform: 'translate(-50%, -50%) rotate(45deg)',
                        width: 9,
                        height: 9,
                        bgcolor: '#fff',
                        border: `1.5px solid ${color}`,
                        cursor: 'pointer',
                        zIndex: 4,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
                        '&:hover': { transform: 'translate(-50%, -50%) rotate(45deg) scale(1.25)' },
                      }}
                    />
                  );
                })}
              </Box>
            );
          })}

          {/* Playhead */}
          {duration > 0 && (
            <>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: 2,
                  left: `${playheadPx}px`,
                  bgcolor: d.accent,
                  pointerEvents: 'none',
                  boxShadow: `0 0 0 1px rgba(255,255,255,0.6)`,
                  zIndex: 3,
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: `${playheadPx}px`,
                  transform: 'translateX(-50%)',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: d.accent,
                  pointerEvents: 'none',
                  zIndex: 3,
                }}
              />
            </>
          )}

          {!hasObjects && (
            <Box
              sx={{
                position: 'absolute',
                inset: '22px 0 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: d.colors.textTertiary,
                fontSize: 12,
              }}
            >
              {t('instat.telestration.emptyTimeline')}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
